import { getInitialValues } from "./utils";

type QueryParamValue = string | number | boolean | Array<string | number | boolean> | null | undefined;

type FormFieldConfig = {
  defaultValue?: unknown;
  controlType?: string;
  props?: {
    options?: Array<{ label: unknown; value: string | number }>;
  };
  isMulti?: boolean;
};

type FormConfig = Record<string, FormFieldConfig>;

export const isEmptyQueryValue = (value: QueryParamValue) => {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "string") return value.trim() === "";
  return false;
};

export const serializeQueryValue = (value: QueryParamValue) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(",");
  }
  return String(value);
};

export const buildSearchParams = (
  previousParams: URLSearchParams,
  values: Record<string, QueryParamValue>,
  keysToReset: string[] = [],
) => {
  const nextParams = new URLSearchParams(previousParams);

  keysToReset.forEach((key) => {
    nextParams.delete(key);
  });

  Object.entries(values).forEach(([key, value]) => {
    if (isEmptyQueryValue(value)) {
      nextParams.delete(key);
      return;
    }
    nextParams.set(key, serializeQueryValue(value));
  });

  return nextParams;
};

export const clearSearchParams = (previousParams: URLSearchParams, keys: string[]) => {
  const nextParams = new URLSearchParams(previousParams);
  keys.forEach((key) => nextParams.delete(key));
  return nextParams;
};

const getSelectValueFromQuery = (fieldConfig: FormFieldConfig, rawValue: string) => {
  const options = fieldConfig.props?.options ?? [];
  const isMulti = fieldConfig.isMulti === true;

  if (isMulti) {
    const selectedValues = rawValue
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);

    return options.filter((option) => selectedValues.includes(String(option.value)));
  }

  return options.find((option) => String(option.value) === rawValue) ?? { label: rawValue, value: rawValue };
};

export const getFilterFormValuesFromParams = (formConfig: FormConfig, searchParams: URLSearchParams) => {
  const defaultValues = getInitialValues(formConfig);

  return Object.entries(formConfig).reduce<Record<string, unknown>>((accumulator, [fieldName, fieldConfig]) => {
    const rawValue = searchParams.get(fieldName);

    if (rawValue == null || rawValue === "") {
      accumulator[fieldName] = defaultValues[fieldName];
      return accumulator;
    }

    if (fieldConfig.controlType === "select") {
      accumulator[fieldName] = getSelectValueFromQuery(fieldConfig, rawValue);
      return accumulator;
    }

    accumulator[fieldName] = rawValue;
    return accumulator;
  }, {});
};

export const getRawFilterValuesFromForm = (formConfig: FormConfig, values: Record<string, any>) => {
  return Object.entries(formConfig).reduce<Record<string, QueryParamValue>>((accumulator, [fieldName, fieldConfig]) => {
    const value = values?.[fieldName];

    if (value == null || value === "") {
      return accumulator;
    }

    if (fieldConfig.controlType === "select") {
      if (Array.isArray(value)) {
        const selectedValues = value.map((item) => item?.value).filter((item) => !isEmptyQueryValue(item));

        if (selectedValues.length) {
          accumulator[fieldName] = selectedValues as Array<string | number>;
        }
        return accumulator;
      }

      if (value?.value != null && value?.value !== "") {
        accumulator[fieldName] = value.value;
      }
      return accumulator;
    }

    if (typeof value === "string" && value.trim() === "") {
      return accumulator;
    }

    accumulator[fieldName] = value;
    return accumulator;
  }, {});
};
